import type { ConditionClause, ConditionExpr } from './condition-eval'
import { evaluateParsedExpr, parseExpr } from './condition-eval'
import { formulaVariables } from './formula-eval'

/**
 * ⛔ EL `showWhen` DE UN CAMPO: UNA SOLA LECTURA PARA LOS TRES LADOS.
 *
 * El editor lo escribe, el compilador lo reescribe al recablear el grafo y lo valida al publicar, y
 * el runtime decide con él si un campo se pide o no. Tres lugares que tienen que estar de acuerdo
 * sobre qué dice la cadena — y el desacuerdo no se ve: el síntoma es un campo que administración
 * configuró para que apareciera y que en el expediente no aparece nunca (o al revés, uno que frena
 * una etapa aunque su condición sea falsa).
 *
 * ── EL TIPO DE LA CONEXIÓN NO CAMBIA ─────────────────────────────────────────
 *
 * `showWhen` sigue siendo `string | null` en el contrato. Lo que cambia es QUÉ dice esa cadena:
 *
 * | forma | significado | de dónde sale |
 * |---|---|---|
 * | `null` / vacío | siempre visible | el campo nace así |
 * | `{{ etapa4.banco }}` | LEGACY: `banco` es verdadero | lo que escribió el selector de variable |
 * | `{"join":"and","clauses":[…]}` | la condición compuesta | el constructor de condiciones |
 *
 * El legacy se ACEPTA en todos lados y no se migra con un script: `normalizeShowWhen` lo convierte
 * cuando el editor abre el borrador y `rewriteShowWhenVariables` cuando el compilador lo toca. Una
 * versión publicada hace meses no se reescribe sola, y por eso `parseShowWhen` lo sigue entendiendo.
 *
 * ── FAIL-OPEN, IGUAL QUE `evaluateExpr` ──────────────────────────────────────
 *
 * Una cadena ilegible se lee como `null` (siempre visible), NO como «oculto». Un campo escondido por
 * un JSON roto es un requisito que desaparece sin que nadie se entere; uno de más se ve y se
 * corrige. Es el mismo criterio del condicional del documento.
 */

/** El `{{ token }}` pelado de la primera versión. Anclado: el valor entero es UN token o no lo es. */
const LEGACY_TOKEN = /^\{\{\s*([a-zA-Z0-9._-]+)\s*\}\}$/

/**
 * Una cláusula «configurada» es la que dice algo. Espeja el filtro de `evaluateExpr`: si el
 * evaluador la ignora, para nosotros tampoco existe — así el editor guarda `null` cuando alguien
 * abre el constructor y lo cierra sin elegir nada.
 */
const isConfigured = (clause: ConditionClause): boolean =>
  clause?.kind === 'expression' ? Boolean(clause.expr?.trim()) : Boolean(clause?.variable)

const legacyExpr = (variable: string): ConditionExpr => ({
  join: 'and',
  clauses: [{ variable, operator: 'truthy' }]
})

/**
 * La condición de visibilidad de un campo, en la forma que entienden el editor y el runtime.
 *
 * `null` significa SIEMPRE VISIBLE, y hay tres caminos hasta ese null: el valor vacío, el JSON
 * ilegible y la condición cuyas cláusulas están todas en blanco. Los tres significan lo mismo para
 * quien tiene que decidir si pinta el campo, así que se devuelven iguales.
 */
export const parseShowWhen = (value: string | null | undefined): ConditionExpr | null => {
  const raw = value?.trim()
  if (!raw) return null

  const legacy = LEGACY_TOKEN.exec(raw)
  if (legacy) return legacyExpr(legacy[1])

  const expr = parseExpr(raw)
  const clauses = expr.clauses.filter(isConfigured)
  return clauses.length > 0 ? { join: expr.join, clauses } : null
}

/**
 * Los tokens que una condición de visibilidad referencia — los de cláusula simple y los de dentro
 * de una fórmula.
 *
 * Lo usa el publish para validar el linaje. Los de la fórmula salen del MISMO jsep que evalúa: un
 * regex propio sería un segundo parser, y el día que uno acepte algo que el otro no, el editor deja
 * publicar un campo cuya condición después no resuelve.
 */
export const showWhenVariables = (value: string | null | undefined): string[] =>
  exprVariables(parseShowWhen(value))

/** Los tokens de una condición YA parseada. La mitad que reusa el evaluador para no parsear dos veces. */
const exprVariables = (expr: ConditionExpr | null): string[] => {
  if (!expr) return []
  const found = new Set<string>()
  for (const clause of expr.clauses) {
    if (clause.kind === 'expression') {
      for (const token of formulaVariables(clause.expr ?? '')) found.add(token)
      continue
    }
    if (clause.variable) found.add(clause.variable)
  }
  return [...found]
}

/**
 * Los HECHOS contra los que se decide si un campo se ve.
 *
 * ☠️ Las tres claves de la semántica están en el TIPO del valor y en la presencia de la clave:
 *
 * | | qué significa | cómo se evalúa |
 * |---|---|---|
 * | la clave NO está | el proceso no produce esa variable | condición ILEGIBLE → el campo se ve |
 * | la clave está en `null` | la variable existe y todavía no se respondió | se compara contra vacío |
 * | la clave tiene valor | se respondió | se compara contra el valor |
 *
 * La diferencia entre las dos primeras es toda la seguridad del mecanismo: sin ella, condicionar un
 * campo a una variable que no existe lo escondería para siempre, y con él se iría su requisito.
 */
export type ShowWhenFacts = Record<string, string | number | boolean | null>

/**
 * ⛔ ¿SE VE ESTE CAMPO? La única respuesta, para los tres lados.
 *
 * La corren el kernel (para no exigir un campo oculto), la vista del expediente (para publicar los
 * mismos hechos que usó el kernel) y la pantalla (para no pintarlo). Escrita tres veces, la primera
 * corrección las separa — y el síntoma es un campo que la pantalla esconde y el backend exige, o al
 * revés: una etapa que no cierra por un campo que nadie ve.
 *
 * ── FAIL-OPEN, Y SIEMPRE HACIA EL LADO SEGURO ────────────────────────────────
 *
 * Todo lo que no se puede afirmar devuelve `true` (visible), y visible significa que el requisito
 * **se exige**: sin condición, con una condición ilegible, con una condición sin cláusulas o con una
 * que mira una variable que el proceso no produce. Un campo de más se ve y se corrige; uno de menos
 * es un requisito que desapareció sin que nadie se entere.
 *
 * ⚠️ **Sin `typeOf`, a propósito.** Los tres lados tienen que dar lo MISMO, y el único catálogo que
 * los tres pueden construir igual es la lista de tokens: una tabla de tipos que existe en el
 * servidor y no en el navegador es exactamente cómo la pantalla y el backend empiezan a discrepar.
 * Las cláusulas simples —el 99% de los casos— no miran el tipo; una cláusula ƒ compara como texto.
 */
export const isFieldVisible = (
  value: string | null | undefined,
  facts: ShowWhenFacts | null | undefined
): boolean => {
  const expr = parseShowWhen(value)
  if (!expr) return true

  const resolved = facts ?? {}
  for (const token of exprVariables(expr)) {
    // `hasOwnProperty.call` y no `in`: `in` recorre la cadena de prototipos, así que un token
    // llamado `constructor` o `toString` daría presente con un valor que no es un hecho de nadie.
    if (!Object.hasOwn(resolved, token)) return true
  }
  return evaluateParsedExpr(expr, comparableFacts(resolved))
}

/**
 * Los hechos, en el texto con el que compara `evaluateCondition`.
 *
 * `null` —la variable existe y no se respondió— baja a cadena vacía, que es como el evaluador ya
 * trata un hecho ausente: `empty` se cumple, `equals` no. Números y booleanos bajan por `String`,
 * el mismo criterio de `factOf` en el kernel (`true` → `'true'`).
 */
const comparableFacts = (facts: ShowWhenFacts): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const [token, value] of Object.entries(facts)) {
    out[token] = value === null || value === undefined ? '' : String(value)
  }
  return out
}

/**
 * Renombra las variables de una condición de visibilidad y la devuelve serializada.
 *
 * Lo llama el compilador cuando recablear el grafo movió la etapa que produce un campo: el token
 * `etapa4.banco` pasa a nombrar otra etapa y la condición decidiría con el campo equivocado, sin un
 * solo error.
 *
 * ⚠️ QUÉ SE RE-SERIALIZA Y QUÉ NO. Un JSON válido al que el renombre no le tocó nada vuelve BYTE A
 * BYTE como entró: re-serializarlo reordenaría claves y ensuciaría el diff de cada publicación con
 * cambios que nadie hizo. El legacy es la excepción y es a propósito — se normaliza aunque no cambie
 * ninguna variable, porque ésta es la única pasada que lo puede jubilar.
 *
 * Lo ilegible se devuelve TAL CUAL. No es nuestro para arreglarlo y borrarlo perdería lo que alguien
 * escribió; el publish lo trata como «sin condición» y sigue.
 */
export const rewriteShowWhenVariables = (
  value: string | null | undefined,
  rename: (variable: string) => string
): string | null => {
  const raw = value?.trim()
  if (!raw) return null

  const legacy = LEGACY_TOKEN.exec(raw)
  if (legacy) return JSON.stringify(legacyExpr(rename(legacy[1])))

  const expr = parseExpr(raw)
  if (expr.clauses.length === 0) return value ?? null

  let changed = false
  const clauses = expr.clauses.map((clause): ConditionClause => {
    if (clause.kind === 'expression') {
      const before = clause.expr ?? ''
      const after = renameFormulaTokens(before, rename)
      if (after === before) return clause
      changed = true
      return { ...clause, expr: after }
    }
    if (!clause.variable) return clause
    const after = rename(clause.variable)
    if (after === clause.variable) return clause
    changed = true
    return { ...clause, variable: after }
  })

  return changed ? JSON.stringify({ join: expr.join, clauses }) : (value ?? null)
}

/**
 * El legacy `{{ token }}` convertido a JSON, sin renombrar nada.
 *
 * Lo corre el editor al abrir el borrador: de ahí para adelante el borrador sostiene JSON-o-null y
 * el constructor de condiciones no tiene que saber que el formato viejo existió.
 */
export const normalizeShowWhen = (value: string | null | undefined): string | null =>
  rewriteShowWhenVariables(value, (variable) => variable)

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Renombra tokens DENTRO de una fórmula, en UNA sola pasada.
 *
 * ☠️ La pasada única no es una optimización: reordenar dos etapas produce un renombre CRUZADO
 * (`etapa1.banco → etapa2.banco` y `etapa2.banco → etapa1.banco`). Reemplazando de a un token, el
 * segundo reemplazo pisa lo que acaba de escribir el primero y las dos variables terminan
 * apuntando a la misma etapa. Es la misma razón por la que el reemplazo de `{{ … }}` del compilador
 * usa un solo `replace` con callback.
 *
 * Los límites se piden a mano —`(^|[^\w.$])` adelante, un lookahead atrás— porque `\b` cortaría
 * `etapa1.banco` en dos y renombraría el `etapa1` de `etapa10.banco`.
 *
 * ⚠️ Lo que sí puede tocar de más: un token escrito DENTRO de un literal de texto
 * (`x == "etapa1.banco"`). Es texto que se compara, no una referencia, y renombrarlo cambia la
 * comparación. Se acepta: distinguirlo pediría reconstruir la fórmula desde el AST —perdiendo el
 * formato que escribió el redactor— y el caso no existe en los datos.
 */
const renameFormulaTokens = (expr: string, rename: (variable: string) => string): string => {
  // Más largos primero: con `a` y `a.b` en la alternancia, `a` ganaría y dejaría `.b` colgando.
  const tokens = [...new Set(formulaVariables(expr))].sort((a, b) => b.length - a.length)
  if (tokens.length === 0) return expr
  const pattern = new RegExp(
    `(^|[^\\w.$])(${tokens.map(escapeForRegExp).join('|')})(?![\\w.$])`,
    'g'
  )
  return expr.replace(pattern, (match, prefix: string, token: string) => {
    const next = rename(token)
    return next === token ? match : `${prefix}${next}`
  })
}
